import { getOccupancyRoomPrice } from "./occupancyPrices";

/**
 * Guests covered by the rooms in the cart: each row is booked for `index`
 * guests, `count` times.
 */
export function getSelectedGuests(rooms: unknown): number {
  if (!Array.isArray(rooms)) {
    return 0;
  }

  return rooms.reduce((total: number, room: any) => {
    const guests = Number(room?.index) * Number(room?.count);

    return Number.isFinite(guests) && guests > 0 ? total + guests : total;
  }, 0);
}

/**
 * An aparthotel group can be split across apartments, so the selected rooms
 * have to host every searched guest before the booking can continue.
 */
export function isGroupCovered(
  property: any,
  rooms: unknown,
  guests: number
): boolean {
  if (property?.property_type !== "apart_hotel") {
    return true;
  }

  return getSelectedGuests(rooms) >= guests;
}

/**
 * Occupancy rows a room type can actually host; rows above its capacity are
 * left out. Without a known capacity every row is kept.
 */
export function limitToCapacity(
  occupancies: number[],
  capacity: unknown
): number[] {
  const limit = Number(capacity);
  if (!Number.isFinite(limit) || limit < 1) {
    return occupancies;
  }

  return occupancies.filter((occupancy) => occupancy <= limit);
}

/**
 * Guests shown in the single row of a room type without per-occupancy prices:
 * the searched guests, at most the room capacity.
 */
export function fallbackOccupancy(guests: number, capacity: unknown): number {
  const searched = guests > 0 ? guests : 1;
  const limit = Number(capacity);

  return Number.isFinite(limit) && limit >= 1
    ? Math.min(searched, limit)
    : searched;
}

/**
 * Dropdowns shown for one room type: one per selected apartment, plus an
 * empty one while more apartments of that type are still free.
 */
export function visibleSlots(
  selections: number[],
  remaining: number
): (number | null)[] {
  return selections.length < remaining ? [...selections, null] : selections;
}

/**
 * Applies a dropdown change. Choosing nothing frees the apartment, so the
 * dropdowns below move up. Returns the occupancies to take out of and put
 * into the cart.
 */
export function changeSlot(
  selections: number[],
  slot: number,
  guests: number | null
): { next: number[]; removed: number | null; added: number | null } {
  const current = selections[slot] ?? null;
  if (current === guests) {
    return { next: selections, removed: null, added: null };
  }

  const next = [...selections];
  if (guests === null) {
    next.splice(slot, 1);
  } else {
    next[slot] = guests;
  }

  return { next, removed: current, added: guests };
}

/**
 * Dropdown option for an occupancy, e.g. "2 hostia · 204€".
 */
export function occupancyOptionLabel(guests: string, price: string): string {
  return `${guests} · ${price}`;
}

/**
 * Occupancies of one room type already in the cart, one per apartment. The
 * dropdowns start from them, so a reloaded availability box keeps showing
 * what is booked.
 */
export function cartSelections(rooms: unknown, roomTypeId: unknown): number[] {
  if (!Array.isArray(rooms)) {
    return [];
  }

  return rooms
    .filter((room: any) => room?.room_type_id === roomTypeId)
    .flatMap((room: any) => {
      const guests = Number(room?.index);
      const count = Number(room?.count);

      return Number.isInteger(guests) && guests > 0 && Number.isInteger(count) && count > 0
        ? Array(count).fill(guests)
        : [];
    });
}

export type GuestCoverage = "none" | "missing" | "exact" | "over";

/**
 * How the apartments picked in an aparthotel cover the searched guests.
 */
export function guestCoverage(selected: number, guests: number): GuestCoverage {
  if (selected <= 0) {
    return "none";
  }
  if (selected < guests) {
    return "missing";
  }

  return selected === guests ? "exact" : "over";
}

/**
 * Apartments picked in the cart: each row is booked `count` times.
 */
export function getSelectedApartments(rooms: unknown): number {
  if (!Array.isArray(rooms)) {
    return 0;
  }

  return rooms.reduce((total: number, room: any) => {
    const count = Number(room?.count);

    return Number.isFinite(count) && count > 0 ? total + count : total;
  }, 0);
}

/**
 * A single apartment: one bookable unit, so the guest does not pick it by hand.
 * Aparthotels and hotels keep the room choice.
 */
export function isSingleApartment(property: any): boolean {
  const type = property?.property_type;

  return (
    (type === "apartment" || type === "single") &&
    Array.isArray(property?.room_types) &&
    property.room_types.length === 1
  );
}

/**
 * Whether the cart of this property follows the guests and dates on its own:
 * an apartment with its unit available, or with none available for the search.
 */
export function selectsApartmentAutomatically(property: any): boolean {
  const type = property?.property_type;

  return (
    (type === "apartment" || type === "single") &&
    Array.isArray(property?.room_types) &&
    property.room_types.length <= 1
  );
}

/**
 * Cart rooms of a single apartment for the searched guests: the apartment
 * booked once, priced for the guests it hosts. Empty when it is not bookable.
 */
export function apartmentCartRooms(property: any, guests: number): any[] {
  if (!isSingleApartment(property)) {
    return [];
  }

  const roomType = property.room_types[0];
  const occupancy = fallbackOccupancy(guests, roomType?.default_occupancy);
  const price = getOccupancyRoomPrice(roomType, occupancy);
  if (!roomType?.id || price <= 0) {
    return [];
  }

  return [
    {
      room_type_id: roomType.id,
      index: occupancy,
      count: 1,
      price,
      name: roomType.title,
    },
  ];
}
